// apps/accounts/src/pages/ProjectVendorRates.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import pb from '../lib/pocketbaseClient';
import toast from 'react-hot-toast';

export default function ProjectVendorRates() {
  const [rates, setRates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    project: '',
    vendor: '',
    category: '',
    clientRate: '',
    vendorRate: '',
    description: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ratesRes, projectsRes, vendorsRes, categoriesRes] = await Promise.all([
        pb.collection('project_vendor_rates').getFullList({
          sort: '-created',
          expand: 'project,vendor,category'
        }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('work_categories').getFullList({ sort: 'name' }),
      ]);
      
      console.log('Projects:', projectsRes);
      console.log('Vendors:', vendorsRes);
      console.log('Categories:', categoriesRes);
      console.log('Rates:', ratesRes);
      
      setRates(ratesRes);
      setProjects(projectsRes);
      setVendors(vendorsRes);
      setCategories(categoriesRes);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      // Log form data
      console.log('Form Data:', formData);
      
      // Validate
      if (!formData.project) {
        setFormError('Please select a project');
        setFormLoading(false);
        return;
      }
      if (!formData.vendor) {
        setFormError('Please select a vendor');
        setFormLoading(false);
        return;
      }
      if (!formData.category) {
        setFormError('Please select a category');
        setFormLoading(false);
        return;
      }

      if (!formData.clientRate || parseFloat(formData.clientRate) <= 0) {
        setFormError('Please enter a valid client rate');
        setFormLoading(false);
        return;
      }
      if (!formData.vendorRate || parseFloat(formData.vendorRate) <= 0) {
        setFormError('Please enter a valid vendor rate');
        setFormLoading(false);
        return;
      }

      // Check if all IDs exist
      const projectExists = projects.find(p => p.id === formData.project);
      const vendorExists = vendors.find(v => v.id === formData.vendor);
      const categoryExists = categories.find(c => c.id === formData.category);

      console.log('Project exists:', projectExists);
      console.log('Vendor exists:', vendorExists);
      console.log('Category exists:', categoryExists);

      if (!projectExists) {
        setFormError('Selected project does not exist');
        setFormLoading(false);
        return;
      }
      if (!vendorExists) {
        setFormError('Selected vendor does not exist');
        setFormLoading(false);
        return;
      }
      if (!categoryExists) {
        setFormError('Selected category does not exist');
        setFormLoading(false);
        return;
      }

      const data = {
        project: formData.project,
        vendor: formData.vendor,
        category: formData.category,
        clientRate: parseFloat(formData.clientRate) || 0,
        vendorRate: parseFloat(formData.vendorRate) || 0,
        description: formData.description || '',
      };

      console.log('Submitting data:', JSON.stringify(data, null, 2));

      let result;
      if (editing) {
        result = await pb.collection('project_vendor_rates').update(editing.id, data);
        toast.success('Rate updated successfully');
      } else {
        result = await pb.collection('project_vendor_rates').create(data);
        toast.success('Rate added successfully');
      }
      
      console.log('Success result:', result);
      handleClose();
      fetchData();
    } catch (error) {
      console.error('=== FULL ERROR DETAILS ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error status:', error.status);
      console.error('Error data:', error.data);
      
      // PocketBase specific error
      if (error.data?.data) {
        console.error('Field errors:', JSON.stringify(error.data.data, null, 2));
        
        // Format field errors for display
        let errorMessages = [];
        Object.entries(error.data.data).forEach(([key, val]) => {
          if (typeof val === 'object' && val.message) {
            errorMessages.push(`${key}: ${val.message}`);
          } else if (typeof val === 'object') {
            errorMessages.push(`${key}: ${JSON.stringify(val)}`);
          } else {
            errorMessages.push(`${key}: ${val}`);
          }
        });
        
        setFormError(`Field errors:\n${errorMessages.join('\n')}`);
      } else if (error.response) {
        console.error('Response:', error.response);
        setFormError(`Server error: ${error.response?.data?.message || error.message}`);
      } else {
        setFormError(`Error: ${error.message || 'Unknown error'}`);
      }
      
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rate?')) return;
    try {
      await pb.collection('project_vendor_rates').delete(id);
      toast.success('Rate deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rate');
    }
  };

  const handleEdit = (rate) => {
    setEditing(rate);
    setFormData({
      project: rate.project,
      vendor: rate.vendor,
      category: rate.category,
      clientRate: rate.clientRate || '',
      vendorRate: rate.vendorRate || '',
      description: rate.description || '',
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      project: '',
      vendor: '',
      category: '',
      clientRate: '',
      vendorRate: '',
      description: '',
    });
    setFormError('');
    setFormLoading(false);
  };

  const filteredRates = rates.filter(r => {
    const matchProject = filterProject === 'all' || r.project === filterProject;
    const matchVendor = filterVendor === 'all' || r.vendor === filterVendor;
    return matchProject && matchVendor;
  });

  const getCategoryUnit = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.unit : '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Project and Vendor Rates</h1>
          <p className="text-sm text-slate-500">Set specific rates for each project, vendor, and work category</p>
        </div>
        <Button 
          className="bg-amber-500 hover:bg-amber-600 text-white"
          onClick={() => setShowModal(true)}
        >
          Add Rate
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border">
        <select 
          value={filterProject} 
          onChange={(e) => setFilterProject(e.target.value)} 
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="all">All Projects</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select 
          value={filterVendor} 
          onChange={(e) => setFilterVendor(e.target.value)} 
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="all">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
        <Button 
          variant="outline" 
          onClick={() => { setFilterProject('all'); setFilterVendor('all'); }}
        >
          Clear
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Project</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Vendor</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Category</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Unit</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Client Rate</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Vendor Rate</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Profit/Unit</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredRates.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                    No rates found. Add rates for projects and vendors.
                  </td>
                </tr>
              ) : (
                filteredRates.map(r => {
                  const unit = getCategoryUnit(r.category);
                  const profit = (r.clientRate || 0) - (r.vendorRate || 0);
                  return (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{r.expand?.project?.name || 'N/A'}</td>
                      <td className="px-4 py-3">{r.expand?.vendor?.name || 'N/A'}</td>
                      <td className="px-4 py-3">{r.expand?.category?.name || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <Badge className="bg-blue-100 text-blue-700">{unit}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600">
                        Rs.{r.clientRate?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-amber-600">
                        Rs.{r.vendorRate?.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {profit >= 0 ? '+' : ''}Rs.{profit.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center space-x-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-amber-600 hover:text-amber-700"
                          onClick={() => handleEdit(r)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(r.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              {editing ? 'Edit Rate' : 'Add Rate'}
            </h2>
            
            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">
                {formError}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Project <span className="text-red-500">*</span>
                  </label>
                  <select 
                    value={formData.project} 
                    onChange={(e) => setFormData({...formData, project: e.target.value})} 
                    className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" 
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Vendor <span className="text-red-500">*</span>
                  </label>
                  <select 
                    value={formData.vendor} 
                    onChange={(e) => setFormData({...formData, vendor: e.target.value})} 
                    className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" 
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({...formData, category: e.target.value})} 
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" 
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.unit})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Client Rate (Rs.) <span className="text-red-500">*</span>
                  </label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={formData.clientRate} 
                    onChange={(e) => setFormData({...formData, clientRate: e.target.value})} 
                    required 
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Vendor Rate (Rs.) <span className="text-red-500">*</span>
                  </label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={formData.vendorRate} 
                    onChange={(e) => setFormData({...formData, vendorRate: e.target.value})} 
                    required 
                    placeholder="0.00"
                  />
                </div>
              </div>

              {formData.clientRate && formData.vendorRate && (
                <div className="bg-gradient-to-r from-emerald-50 to-amber-50 p-4 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Profit per Unit:</span>
                    <span className={`text-lg font-bold ${parseFloat(formData.clientRate) - parseFloat(formData.vendorRate) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {parseFloat(formData.clientRate) - parseFloat(formData.vendorRate) >= 0 ? '+' : ''}
                      Rs.{(parseFloat(formData.clientRate) - parseFloat(formData.vendorRate)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <Input 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  placeholder="Optional notes..." 
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-500 hover:bg-amber-600 text-white" 
                  disabled={formLoading}
                >
                  {formLoading ? 'Saving...' : (editing ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}